import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActionSheetController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { ActionService } from 'src/app/services/account/privilege/action.service';
import { AccountInfoService } from 'src/app/services/account/account-info.service';
import { AccountCoupleService } from 'src/app/services/account/account-couple.service';
import { AccountUserService } from 'src/app/services/account/account-user.service';
import { AccountLoginService } from 'src/app/services/account/account-login.service';
import { OnlineService } from 'src/app/services/general/online.service';
import { PopupService } from 'src/app/services/general/popup.service';

import { UserRole, AccountUser, AccountCouple, CoupleList } from 'src/app/interfaces/account';
import { AccountLogin } from 'src/app/interfaces/login';

import { AccountUserComponent } from 'src/app/components/account/account-user/account-user.component';
import { AccountShareComponent } from 'src/app/components/account/account-share/account-share.component';

/**
 * Couple list component
 */
@Component({
  selector: 'app-account-couple',
  templateUrl: './account-couple.component.html',
  styleUrls: ['./account-couple.component.scss'],
})
export class AccountCoupleComponent implements OnInit, OnDestroy {

  ready: boolean;
  /**
   * Setup mode flag for first time couple list setup
   */
  setupMode: boolean;
  /**
   * Couple type list
   */
  accountCoupleList: AccountCouple[];
  /**
   * Couple list
   */
  coupleList: CoupleList[];

  coupleNameList: {[coupleId: string] : string};
  sameCoupleType: boolean;

  /**
   * Couple user list
   */
  private coupleUserList: AccountUser[];
  /**
   * Couple type subscription
   */
  private accountCoupleSubscription: Subscription;
  /**
   * Couple list subscription
   */
  private coupleListSubscription: Subscription;

  private accountInfoSubscription: Subscription;

  /**
   * Constructor
   * @param actionSheetController Action Sheet Controller
   * @param modalController Modal Controller
   * @param navParams Navigation Params
   * @param translate Translate Service
   * @param accountInfoService Account info service
   * @param accountUserService Account User Service
   * @param accountCoupleService Couple Service
   * @param actionService Action Service
   * @param accountLoginService Account Login Service
   * @param onlineService Online Service
   * @param popupService Popup Service
   */
  constructor(
    private actionSheetController: ActionSheetController,
    private modalController: ModalController,
    private translate: TranslateService,
    private accountInfoService: AccountInfoService,
    private accountUserService: AccountUserService,
    private accountCoupleService: AccountCoupleService,
    private actionService: ActionService,
    private accountLoginService: AccountLoginService,
    private onlineService: OnlineService,
    private popupService: PopupService,
  ) {
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.unwatch();
  }

  /**
   * Before view enter, get setup mode as params
   */
  async ionViewWillEnter() {
    this.ready = true;
    this.watch();
  }

  /**
   * Before view leave, unwatch observable
   */
  ionViewWillLeave() {
    this.ready = false;
    this.unwatch();
  }

  watch() {
    this.watchAccountCouple();
    this.watchCoupleList();
    this.watchAccountInfo();
  }

  unwatch() {
    this.unwatchAccountCouple();
    this.unwatchCoupleList();
    this.unwatchAccountInfo();
  }

  /**
   * Watch couple type and couple list
   */
  async watchAccountCouple() {
    if (!this.accountCoupleSubscription) {
      this.accountCoupleSubscription = this.accountCoupleService.observableCoupleType.subscribe((accountCouple: AccountCouple[]) => {
        this.accountCoupleList = accountCouple;
        this.sameCoupleType = this.checkSameCoupleType();
        this.setupCoupleName();
      });
    }
  }

  watchCoupleList() {
    if (!this.coupleListSubscription) {
      this.coupleListSubscription = this.accountCoupleService.observableCoupleUserList.subscribe((coupleUserList: AccountUser[]) => {
        this.coupleUserList = coupleUserList;
        this.setupCoupleName();
      });
    }
  }

  /**
   * Unwatch couple type and couple list
   */
  unwatchAccountCouple() {
    if (this.accountCoupleSubscription) {
      this.accountCoupleSubscription.unsubscribe();
      this.accountCoupleSubscription = null;
    }
   
  }

  unwatchCoupleList() {
    if (this.coupleListSubscription) {
      this.coupleListSubscription.unsubscribe();
      this.coupleListSubscription = null;
    }
  }

  watchAccountInfo() {
    if (!this.accountInfoSubscription) {
      this.accountInfoSubscription = this.accountInfoService.observableAccountInfo.subscribe(() => {
        this.setupCoupleName();
      })
    }
  }

  unwatchAccountInfo() {
    if (this.accountInfoSubscription) {
      this.accountInfoSubscription.unsubscribe();
      this.accountInfoSubscription = null;
    }
  }

  /**
   * Get couple user by couple id
   * @param coupleId Couple Id
   * @returns Account user
   */
  getCoupleUser(coupleId: number): AccountUser {
    if (coupleId && this.coupleUserList?.length) {
      const coupleIndex = this.coupleUserList?.findIndex((x: AccountUser) => x?.role?.coupleId === coupleId);
      if (coupleIndex !== -1) {
        return this.coupleUserList[coupleIndex];
      }
    }
    return null;
  }

  /**
   * Get couple name by couple iu
   * @param coupleId couple id
   * @returns couple name
   */
  getCoupleName(coupleId: number): string {
    if (coupleId && this.coupleList?.length) {
      const coupleListIndex = this.coupleList?.findIndex((x: CoupleList) => x?.accountCouple?.coupleId === coupleId);
      if (coupleListIndex !== -1) {
        return this.coupleList[coupleListIndex].name;
      }
    }
    return '';
  }

  setupCoupleName() {
    if (!this.coupleNameList) {
      this.coupleNameList = {};
    }
    this.accountCoupleList.forEach((accountCouple: AccountCouple) => {
      this.coupleNameList[accountCouple.coupleId] = this.getCoupleName(accountCouple.coupleId);
    });
  }

  /**
   * Check if same couple type applied
   * @returns True if same couple type
   */
  checkSameCoupleType(): boolean {
    return this.accountCoupleService.checkSameCoupleType();
  }

  /**
   * Dismiss modal
   */
  async dismissModal() {
    if (this.modalController) {
      const modal = await this.modalController.getTop();
      if (modal) { await modal.dismiss(); }
    }
  }

  /**
   * Present account share modal
   * @param accountLogin account login
   */
  async presentAccountShareModal(accountLogin: AccountLogin) {
    if (accountLogin) {
      const modal = await this.modalController.create({
        component: AccountShareComponent,
        cssClass: 'modal-full-screen-bk',
        componentProps: {
          accountLogin
        }
      });
      modal.present();
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Present account user modal to select new couple
   * @param coupleId Couple ID
   * @param slidingItem Sliding Item (Optional)
   */
  async presentAccountUserModal(coupleId: number, slidingItem?: any) {
    if (coupleId) {
      let selectedUid: string;
      const coupleIndex = this.coupleUserList?.findIndex((x: AccountUser) => x?.role?.coupleId === coupleId);
      if (coupleIndex !== -1) {
        selectedUid = this.coupleUserList[coupleIndex]?.uid;
      }
      const modal = await this.modalController.create({
        component: AccountUserComponent,
        componentProps: { selectedUid }
      });
      modal.present();
      modal.onWillDismiss().then(() => {
        if (slidingItem) { slidingItem.close(); }
      });
      modal.onDidDismiss().then((accountUserData: any) => {
        if (accountUserData?.data?.uid) {
          this.updateCouple(coupleId, accountUserData.data.uid);
        }
      });
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Invite new couple modal
   * @param coupleId Couple ID
   */
  async inviteNewCouple(coupleId: number) {
    if (this.onlineService.isOnline()) {
      if (coupleId) {
        await this.popupService.presentLoading(this.translate.instant('LOADING.new_access'));
        const role: UserRole = {
          type: this.accountCoupleService.getCoupleType(coupleId),
          custom: false,
          coupleId
        };
        const accountLogin = await this.accountLoginService.generateAccountLoginCall(
          role, this.actionService.actionList,
          this.accountInfoService?.accountInfo?.title?.value,
          this.accountInfoService?.accountInfo?.time,
          this.accountInfoService?.accountInfo?.timezone,
        );
        await this.popupService.dismissLoading();
        if (accountLogin) {
          this.presentAccountShareModal(accountLogin);
        }
      } else {
        this.popupService.presentActionError();
      }
    }
  }

  /**
   * Check invite type, for setup mode, present invite new modal directly, else present action sheet
   * @param coupleId Couple ID
   */
  checkInviteType(coupleId: number) {
    if (this.onlineService.isOnline()) {
      if (coupleId) {
        if (this.setupMode) {
          this.inviteNewCouple(coupleId);
        } else {
          this.presentInviteActionSheet(coupleId);
        }
      } else {
        this.popupService.presentActionError();
      }
    }
  }

  /**
   * Present couple name prompt
   * @param accountCouple account couple
   * @param coupleName couple name
   */
  async presentCoupleNamePrompt(accountCouple: AccountCouple, coupleName: string) {
    if (accountCouple?.coupleId && accountCouple?.coupleType) {
      const modal = await this.popupService.presentInput(
        this.translate.instant('LIST.wedding_couple_type.' + accountCouple.coupleType), '', '', '', coupleName, true);
      modal.onDidDismiss().then((result: any) => {
        if (result?.data?.input && this.coupleList?.length) {
          const name = result.data.input;
          const index = this.coupleList?.findIndex((x: CoupleList) => x?.accountCouple?.coupleId === accountCouple.coupleId);
          if (index !== -1 && name !== this.coupleList[index].name) {
            this.coupleList[index].name = name;
            if (!this.accountInfoService.accountInfo?.title.custom) {
              const title = {
                value: this.translate.instant('ACCOUNT.auto_title', { couple1: this.getCoupleName(1), couple2: this.getCoupleName(2) })
              };
              this.saveDb({ coupleList: this.coupleList, title });
            } else {
              this.saveDb({ coupleList: this.coupleList });
            }
          } else {
            this.popupService.saveSuccessToast();
          }
        }
      });
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Present invite action sheet
   * @param coupleId Couple ID
   */
  async presentInviteActionSheet(coupleId: number) {
    if (coupleId) {
      let header = this.translate.instant('LBL.invite') + ' '
      + this.translate.instant('LIST.wedding_couple_type.' +  this.accountCoupleService.getCoupleType(coupleId));
      if (this.checkSameCoupleType()) {
        header = header + ' ' + coupleId;
      }
      const actionSheet = await this.actionSheetController.create({
        header,
        buttons: [{
          text: this.translate.instant('CRUD.new_field', { field: this.translate.instant('LBL.user') }),
          icon: 'share-social-outline',
          handler: () => {
            this.inviteNewCouple(coupleId);
          }
        }, {
          text: this.translate.instant('CRUD.existing_field', { field: this.translate.instant('LBL.user') }),
          icon: 'add-outline',
          handler: () => {
            this.presentAccountUserModal(coupleId);
          }
        }, {
          text: this.translate.instant('BTN.cancel'),
          icon: 'close',
          role: 'cancel',
          handler: () => {
          }
        }]
      });
      actionSheet.present();
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Present update action sheet
   * @param coupleId Couple ID
   */
  async presentUpdateActionSheet(coupleId: number) {
    if (coupleId) {
      let header = this.translate.instant('CRUD.update') + ' '
      + this.translate.instant('LIST.wedding_couple_type.' +  this.accountCoupleService.getCoupleType(coupleId));
      if (this.checkSameCoupleType()) {
        header = header + ' ' + coupleId;
      }
      const actionSheet = await this.actionSheetController.create({
        header,
        buttons: [{
          text: this.translate.instant('CRUD.update'),
          icon: 'pencil-outline',
          handler: () => {
            this.presentAccountUserModal(coupleId);
          }
        }, {
          text: this.translate.instant('CRUD.delete'),
          icon: 'trash-outline',
          handler: () => {
            this.deleteCoupleConfirm(coupleId);
          }
        }, {
          text: this.translate.instant('BTN.cancel'),
          icon: 'close',
          role: 'cancel',
          handler: () => {
          }
        }]
      });
      actionSheet.present();
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Delete Couple Confirm prompt
   * @param coupleId Couple ID
   * @param slidingItem Sliding Item
   */
  async deleteCoupleConfirm(coupleId: number, slidingItem?: any) {
    if (coupleId) {
      const modal = await this.popupService.presentConfirm(
        this.translate.instant('ACCOUNT.msg.unassign_wedding_couple')
      );
      modal.onDidDismiss().then((result: any) => {
        if (slidingItem) { slidingItem.close(); }
        if (result?.data?.confirm) {
          this.deleteCouple(coupleId);
        }
      });
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Delete Couple
   * @param coupleId Couple ID
   */
  async deleteCouple(coupleId: number) {
    if (coupleId) {
      const currentCoupleIndex = this.coupleUserList?.findIndex((x: AccountUser) => x?.role?.coupleId === coupleId);
      if (currentCoupleIndex !== -1) {
        const role: UserRole = {
          coupleId: 0,
          custom: false,
          type: 'other'
        };
        await this.popupService.presentLoading();
        await this.accountUserService.updateAccountUser(this.coupleUserList[currentCoupleIndex].uid, { role });
        this.popupService.dismissLoading();
        this.popupService.saveSuccessToast();
      } else {
        this.popupService.saveFailToast();
      }
    } else {
      this.popupService.saveFailToast();
    }
  }

  /**
   * Update Couple
   * @param coupleId Couple ID
   * @param uid UID
   */
  async updateCouple(coupleId: number, uid: string) {
    if (coupleId && uid) {
      await this.popupService.presentLoading();
      await this.deleteCouple(coupleId);

      const role: UserRole = {
        coupleId,
        custom: false,
        type: this.accountCoupleService.getCoupleType(coupleId)
      };
      await this.accountUserService.updateAccountUser(uid, { role });
      this.popupService.dismissLoading();
      this.popupService.saveSuccessToast();
    } else {
      this.popupService.saveFailToast();
    }
  }

  /**
   * Save account info db
   * @param data data
   */
  async saveDb(data: any) {
    if (data) {
      await this.popupService.presentLoading();
      await this.accountInfoService.updateAccountInfo(data);
      this.popupService.dismissLoading();
      this.popupService.saveSuccessToast();
      this.setupCoupleName();
    } else {
      this.popupService.saveFailToast();
    }
  }
}
